/** @jsxImportSource @emotion/react */
import React from "react";
import {
    EPDSScore,
    EPDSQuestion, EPDSQuestionText, EPDSOptionList, EPDSOption, EPDSRadio,
    EPDSResult, EPDSResultTitle, EPDSResultDetail,
    PNCPrimaryBtn, PNCSecondaryBtn, PNCButtonRow,
    PNCFormBadge,
} from "./PostnatalCareStyles";

/* ═══════════════════════════════════════════════════════════════
   EdinburghDepressionScale — Interactive EPDS screening tool
   10-question validated instrument for postnatal depression.
   Score range: 0–30. Threshold ≥10 possible, ≥13 probable.
   Question 10 (self-harm) flagged as critical.
   ═══════════════════════════════════════════════════════════════ */

export var EdinburghDepressionScale = function (props) {
    var questions = props.questions;
    var responses = props.responses;
    var onResponse = props.onResponse;
    var score = props.score;
    var isComplete = props.isComplete;
    var interpretation = props.interpretation;
    var onApply = props.onApply;
    var onCancel = props.onCancel;

    var answeredCount = Object.keys(responses).length;
    var progressPct = Math.round((answeredCount / 10) * 100);

    return (
        <div>
            {/* ─── Status Bar ─── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
                <PNCFormBadge variant={isComplete ? "success" : "warning"}>
                    {answeredCount}/10 answered
                </PNCFormBadge>
                {score !== null && (
                    <EPDSScore score={score}>{score}/30</EPDSScore>
                )}
            </div>

            {/* ─── Instructions ─── */}
            <div style={{
                padding: "12px 16px",
                borderRadius: "8px",
                background: "#f5f7ff",
                border: "1px solid #e0e4ff",
                marginBottom: "16px",
                fontSize: "0.82rem",
                color: "#555",
                lineHeight: 1.6,
            }}>
                <strong style={{ color: "#333" }}>Instructions:</strong> As you have recently had a baby,
                we would like to know how you are feeling. Please select the answer that comes closest to
                how you have felt <strong>in the past 7 days</strong>, not just how you feel today.
            </div>

            {/* ─── Progress Bar ─── */}
            <div style={{
                height: "6px",
                borderRadius: "3px",
                background: "#f0f0f0",
                marginBottom: "20px",
                overflow: "hidden",
            }}>
                <div style={{
                    height: "100%",
                    borderRadius: "3px",
                    background: isComplete ? "#2e7d32" : "#00695c",
                    width: progressPct + "%",
                    transition: "width 0.3s ease",
                }} />
            </div>

            {/* ─── Questions ─── */}
            {questions.map(function (q, idx) {
                var answered = responses[q.id] !== undefined;
                var selectedValue = responses[q.id];
                return (
                    <EPDSQuestion key={q.id} answered={answered} critical={q.isCritical}>
                        <EPDSQuestionText critical={q.isCritical}>
                            <span style={{ opacity: 0.5, marginRight: "8px" }}>{idx + 1}.</span>
                            {q.text}
                            {q.isCritical && (
                                <span style={{
                                    display: "inline-block",
                                    marginLeft: "8px",
                                    padding: "1px 6px",
                                    borderRadius: "4px",
                                    fontSize: "0.65rem",
                                    fontWeight: 700,
                                    background: "#c62828",
                                    color: "#fff",
                                }}>
                                    CRITICAL
                                </span>
                            )}
                        </EPDSQuestionText>
                        <EPDSOptionList>
                            {q.options.map(function (opt) {
                                var isSelected = selectedValue === opt.value;
                                return (
                                    <EPDSOption
                                        key={opt.value + "_" + opt.label}
                                        selected={isSelected}
                                        onClick={function () { onResponse(q.id, opt.value); }}
                                    >
                                        <EPDSRadio selected={isSelected} />
                                        <span>{opt.label}</span>
                                    </EPDSOption>
                                );
                            })}
                        </EPDSOptionList>
                    </EPDSQuestion>
                );
            })}

            {/* ─── Interpretation Result ─── */}
            {isComplete && interpretation && (
                <EPDSResult level={interpretation.level}>
                    <EPDSResultTitle level={interpretation.level}>
                        Score: {score}/30 — {interpretation.label}
                    </EPDSResultTitle>
                    <EPDSResultDetail>{interpretation.detail}</EPDSResultDetail>

                    {/* Critical question 10 warning */}
                    {responses[10] >= 1 && (
                        <div style={{
                            marginTop: "12px",
                            padding: "10px 14px",
                            borderRadius: "8px",
                            background: "#c628280d",
                            border: "1px solid #c6282830",
                        }}>
                            <div style={{ fontWeight: 700, fontSize: "0.85rem", color: "#c62828", marginBottom: "4px" }}>
                                Self-Harm Ideation Detected (Question 10)
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#555" }}>
                                Patient indicated thoughts of self-harm. Conduct immediate safety assessment.
                                Do not leave the patient alone. Refer to mental health professional urgently.
                            </div>
                        </div>
                    )}
                </EPDSResult>
            )}

            {/* ─── Action Buttons ─── */}
            <PNCButtonRow>
                <PNCSecondaryBtn onClick={onCancel}>Cancel</PNCSecondaryBtn>
                <PNCPrimaryBtn
                    disabled={!isComplete}
                    onClick={onApply}
                >
                    {isComplete ? "Apply Score (" + score + "/30)" : "Complete all questions to apply"}
                </PNCPrimaryBtn>
            </PNCButtonRow>
        </div>
    );
};
